/**
 * `<gh-claim-dialog>` - "Claim account" (§AM step 4; owner, 2026-09-24: *"the product should have a
 * "Claim account" or something, present and clearly visible in the UI, so that the user can commit to it
 * after playing around with it for a while"*, and *"claiming it basically just means connecting a login
 * provider to it"*).
 *
 * One dialog, four views, the canvas's boards (https://claude.ai/artifact/8pn2BYXm8z9r7nMgonh4Tm):
 * - `form`: Google, or an emailed link. ⚠ **No password here** - a password would store an address
 *   nobody has proven, which is the squat `services/auth.ts`'s latch exists to stop. One can be added in
 *   Settings afterwards.
 * - `sent`: the link is out; it works on any device, and opened on a phone the work is on the phone too.
 * - `exists`: the address (or the Google account) already has an account. No merge (the owner: *"Merge
 *   would be cool though"* - a later round), so it says what that means and offers the two ways out.
 * - `leave`: what "Sign out" is for a guest - the cookie is their only key, so leaving is deleting, and
 *   the dialog says so before anything happens.
 *
 * ⚠ **A singleton the app mounts on `document.body`** (`frontend/claim.ts`), because it is opened from
 * everywhere - the sidebar, the phone's top bar, and every "claim first" lock in the app - and a dialog
 * with as many hosts as that belongs to none of them. It carries its own Escape for the reason
 * `<gh-mark-rejected>` does: a component's dialog inherits none.
 */
import { css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { GhElement } from "./ghElement.ts";
import { buttonStyles, chromeStyles, controlStyles, focusStyles, formStyles, modalStyles, resetStyles, tooltipStyles } from "./sharedStyles.ts";
import { api } from "../api.ts";
import { t, tKey } from "../i18n.ts";
import { modalClose } from "../modalClose.ts";
import { toast } from "../utils.ts";
import { withNodes } from "../withNodes.ts";
import { icon } from "./ghIcon.ts";
import { authEmailDelivery, googleLoginAvailable } from "../meta.ts";
import { TRIAL_DAYS } from "../../../shared/plans.ts";
import { isEmptyString } from "../../../shared/guards.ts";
import type { BillingState } from "../../../shared/types.ts";

export type ClaimView = "form" | "sent" | "exists" | "leave";

/** The path a claim returns to - where the person was, minus the outcome marker of a previous claim */
function returnPath(): string {
	const url = new URL(location.href);
	url.searchParams.delete("claim");
	return `${url.pathname}${url.search}`;
}

@customElement("gh-claim-dialog")
export class GhClaimDialog extends GhElement {
	/** null = closed. Set by `openClaimDialog()`; state follows it, never the reverse. */
	@property({ attribute: false }) accessor view: ClaimView | null = null;
	/** The address a link went to, or the one refused as already taken */
	@state() private accessor email = "";
	/** The refusal came back from Google rather than from a typed address - a different sentence */
	@state() private accessor fromGoogle = false;
	@state() private accessor busy = false;
	@state() private accessor kept: { artists: number; releases: number } | null = null;

	static styles = [
		focusStyles,
		resetStyles,
		chromeStyles,
		buttonStyles,
		controlStyles,
		formStyles,
		modalStyles,
		tooltipStyles,
		css`
			.claim-content {
				width: min(440px, calc(100vw - 32px));
				display: flex;
				flex-direction: column;
				gap: 14px;
			}
			.claim-content h3 {
				font-family: var(--font-display);
				font-stretch: var(--font-display-stretch);
				font-weight: 700;
				font-size: 1.625rem;
				line-height: 1.1;
			}
			.claim-lead {
				font-size: 0.9375rem;
				color: var(--text2);
			}
			.claim-kept {
				display: flex;
				flex-wrap: wrap;
				gap: 6px;
				margin: 0;
				padding: 0;
				list-style: none;
			}
			.claim-google {
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 10px;
				width: 100%;
				min-height: 44px;
				font-size: 0.9375rem;
				font-weight: 600;
			}
			.claim-or {
				display: flex;
				align-items: center;
				gap: 10px;
				font-size: 0.75rem;
				color: var(--text2);
			}
			.claim-or::before,
			.claim-or::after {
				content: "";
				flex: 1;
				height: 1px;
				background: var(--border);
			}
			.claim-form {
				display: flex;
				flex-direction: column;
				gap: 10px;
			}
			.claim-form .btn-primary,
			.claim-wide {
				width: 100%;
				min-height: 44px;
				justify-content: center;
			}
			.claim-fine {
				font-size: 0.75rem;
				color: var(--text2);
			}
			.claim-fine a {
				color: inherit;
				text-decoration: underline;
			}
			.claim-row {
				display: flex;
				flex-wrap: wrap;
				gap: 8px;
			}
			.claim-mark {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				width: 44px;
				height: 44px;
				border-radius: 50%;
				background: color-mix(in srgb, var(--accent) 14%, transparent);
				color: var(--accent-text);
			}
		`
	];

	#onKeyDown = (event: KeyboardEvent): void => {
		if (event.key !== "Escape" || this.view == null) return;
		if (document.querySelector("dialog[open]") != null) return;
		event.preventDefault();
		this.close();
	};

	connectedCallback(): void {
		super.connectedCallback();
		document.addEventListener("keydown", this.#onKeyDown);
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		document.removeEventListener("keydown", this.#onKeyDown);
	}

	/** Open on a view. `exists` from Google has no typed address to name, which is `fromGoogle`. */
	open(view: ClaimView, options: { fromGoogle?: boolean } = {}): void {
		this.fromGoogle = options.fromGoogle === true;
		if (view !== "exists") this.email = view === "sent" ? this.email : "";
		this.view = view;
		if (this.kept == null) void this.#loadKept();
	}

	close(): void {
		this.view = null;
		this.busy = false;
	}

	/** What they would keep - the counts off the one payload the walls already read */
	async #loadKept(): Promise<void> {
		try {
			const billing = await api<BillingState>("/api/billing");
			this.kept = { artists: billing.artistCount, releases: billing.releaseCount };
		} catch {
			// The chips are a reassurance, not the dialog - a failed read leaves them out
		}
	}

	#google(): void {
		location.href = `/api/auth/google?intent=claim&return=${encodeURIComponent(returnPath())}`;
	}

	/**
	 * The emailed link. A plain `fetch` rather than `api()`, because the dialog has to tell ONE refusal
	 * from the rest - an address that already has an account is a view of its own, not a toast - and
	 * `api()` hands back a sentence and drops the code.
	 */
	async #sendLink(address: string): Promise<void> {
		const email = address.trim();
		if (isEmptyString(email) || this.busy) return;
		this.busy = true;
		try {
			const res = await fetch("/api/auth/claim/magic", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email })
			});
			const body = (await res.json().catch(() => ({}))) as { code?: string; error?: string; errorParams?: Record<string, string | number> };
			this.email = email;
			if (res.ok) {
				this.view = "sent";
			} else if (body.code === "err.claimExists") {
				this.fromGoogle = false;
				this.view = "exists";
			} else {
				toast(typeof body.code === "string" ? tKey({ key: body.code, params: body.errorParams }) : (body.error ?? res.statusText), 5000, "error");
			}
		} catch (e) {
			toast(e instanceof Error ? e.message : String(e), 5000, "error");
		} finally {
			this.busy = false;
		}
	}

	/** "Sign in to my account": this browser's guest session ends here, and the sign-in screen takes over */
	async #signInElsewhere(): Promise<void> {
		await fetch("/api/auth/logout", { method: "POST" }).catch(() => undefined);
		location.href = "/signin";
	}

	async #discard(): Promise<void> {
		if (this.busy) return;
		this.busy = true;
		try {
			await api("/api/auth/guest/discard", { method: "POST" });
			location.href = "/";
		} catch (e) {
			this.busy = false;
			toast(e instanceof Error ? e.message : String(e), 5000, "error");
		}
	}

	#keptChips(): TemplateResult | typeof nothing {
		const kept = this.kept;
		if (kept == null) return nothing;
		const chips = [
			kept.artists > 0 ? t("claim.keptArtists", { count: kept.artists }) : null,
			kept.releases > 0 ? t("claim.keptReleases", { count: kept.releases }) : null
		].filter(chip => chip != null);
		if (chips.length === 0) return nothing;
		return html`<ul class="claim-kept">
			${chips.map(chip => html`<li class="chip">${chip}</li>`)}
		</ul>`;
	}

	#legalLine(key: "claim.terms"): TemplateResult {
		return html`${withNodes(t(key), {
			terms: html`<a href="/terms" target="_blank" rel="noopener">${t("claim.termsLink")}</a>`,
			privacy: html`<a href="/privacy" target="_blank" rel="noopener">${t("claim.privacyLink")}</a>`
		})}`;
	}

	#formView(): TemplateResult {
		return html`<h3>${t("claim.title")}</h3>
			${this.#keptChips()}
			<p class="claim-lead">${t("claim.lead")}</p>
			${googleLoginAvailable()
				? html`<button type="button" class="btn claim-google" data-action="claim-google" @click=${() => this.#google()}>
							${icon("brand-google")} ${t("claim.google")}
						</button>
						<p class="claim-or">${t("claim.or")}</p>`
				: nothing}
			<form
				class="claim-form"
				@submit=${(e: SubmitEvent) => {
					e.preventDefault();
					const input = this.renderRoot.querySelector<HTMLInputElement>("#claim-email");
					void this.#sendLink(input?.value ?? "");
				}}
			>
				<div class="form-group">
					<label for="claim-email">${t("claim.emailLabel")}</label>
					<input type="email" id="claim-email" required autocomplete="email" placeholder="you@example.com" .value=${this.email} />
				</div>
				<button type="submit" class="btn btn-primary" data-action="claim-email" ?disabled=${this.busy}>${icon("mail")} ${t("claim.emailBtn")}</button>
			</form>
			<p class="claim-fine">
				${t("claim.trialLine", { count: TRIAL_DAYS })}
				<span class="tooltip-icon" tabindex="0" data-tooltip=${t("claim.trialTip")}>?</span>
			</p>
			<p class="claim-fine">${this.#legalLine("claim.terms")}</p>`;
	}

	#sentView(): TemplateResult {
		return html`<span class="claim-mark" aria-hidden="true">${icon("mail", { size: 20 })}</span>
			<h3>${t("claim.sentTitle")}</h3>
			<p class="claim-lead">${t("claim.sentBody", { email: this.email })}</p>
			${authEmailDelivery() === "console" ? html`<p class="claim-fine">${t("claim.sentConsole")}</p>` : nothing}
			<p class="claim-lead">${t("claim.sentKeepWorking")}</p>
			<div class="claim-row">
				<button type="button" class="btn" data-action="claim-resend" ?disabled=${this.busy} @click=${() => void this.#sendLink(this.email)}>
					${icon("refresh")} ${t("claim.sendAgain")}
				</button>
				${googleLoginAvailable()
					? html`<button type="button" class="btn" @click=${() => this.#google()}>${icon("brand-google")} ${t("claim.useGoogle")}</button>`
					: nothing}
			</div>
			<p class="claim-fine">${t("claim.sentExpiry")}</p>`;
	}

	#existsView(): TemplateResult {
		return html`<h3>${t("claim.existsTitle")}</h3>
			<p class="claim-lead">${this.fromGoogle ? t("claim.existsBodyGoogle") : t("claim.existsBody", { email: this.email })}</p>
			<button type="button" class="btn btn-primary claim-wide" data-action="claim-other" @click=${() => (this.view = "form")}>
				${t("claim.existsOther")}
			</button>
			<button type="button" class="btn claim-wide" data-action="claim-sign-in" @click=${() => void this.#signInElsewhere()}>
				${t("claim.existsSignIn")}
			</button>
			<p class="claim-fine">${t("claim.existsHint")}</p>`;
	}

	#leaveView(): TemplateResult {
		return html`<h3>${t("claim.leaveTitle")}</h3>
			<p class="claim-lead">${t("claim.leaveBody")}</p>
			${this.#keptChips()}
			<button type="button" class="btn btn-primary claim-wide" data-action="claim-open" @click=${() => (this.view = "form")}
				>${t("claim.button")}</button
			>
			<button
				type="button"
				class="btn btn-ghost-danger claim-wide"
				data-action="claim-discard"
				?disabled=${this.busy}
				@click=${() => void this.#discard()}
			>
				${icon("trash")} ${t("claim.leaveDelete")}
			</button>`;
	}

	protected render(): TemplateResult | typeof nothing {
		const view = this.view;
		if (view == null) return nothing;
		const body = view === "form" ? this.#formView() : view === "sent" ? this.#sentView() : view === "exists" ? this.#existsView() : this.#leaveView();
		return html`<div class="modal" id="modal-claim" data-view=${view}>
			${modalClose(() => this.close())}
			<div class="modal-content claim-content">${body}</div>
		</div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"gh-claim-dialog": GhClaimDialog;
	}
}

/** Show the dialog, creating it on first use - the one way any surface opens it (`frontend/claim.ts`) */
export function showClaimDialog(view: ClaimView = "form", options: { fromGoogle?: boolean } = {}): void {
	let dialog = document.querySelector("gh-claim-dialog");
	if (dialog == null) {
		dialog = document.createElement("gh-claim-dialog");
		document.body.appendChild(dialog);
	}
	dialog.open(view, options);
}
